import { getAppField, hasAppValue } from './applicationFields';

const DOCUMENT_STEP_MAP = [
  { pattern: /sss/i, step: 5 },
  { pattern: /phil\s*health/i, step: 5 },
  { pattern: /pag-?ibig/i, step: 5 },
  { pattern: /tin|bir/i, step: 5 },
  { pattern: /nbi/i, step: 6 },
  { pattern: /coe|clearance|employment/i, step: 6 },
  { pattern: /resume|portfolio/i, step: 6 },
  { pattern: /birth/i, step: 6 },
];

export const hasStringValue = (value) => hasAppValue(value);

export const getAppRequirementIssues = (app) => {
  if (!app) return [];

  const issues = [];
  const hasResume = hasAppValue(getAppField(app, ['resume_file', 'resume_files', 'resume'], ''));

  if (!hasResume) issues.push('Missing Resume');

  return issues;
};

export const formatRequirementLabels = (issues = []) => issues.map((issue) => {
  if (issue.includes('Resume')) return 'Resume';
  if (issue.includes('Birth')) return 'PSA/Birth Certificate';
  if (issue.includes('SSS')) return 'SSS ID / Number';
  if (issue.includes('PhilHealth')) return 'PhilHealth ID / Number';
  if (issue.includes('Pag-IBIG')) return 'Pag-IBIG ID / Number';
  if (issue.includes('TIN')) return 'TIN / BIR ID';
  if (issue.includes('NBI')) return 'NBI Clearance';
  if (issue.includes('COE')) return 'COE/Clearance';
  return issue;
});

export const getStepForDocument = (docLabel) => {
  if (!docLabel) return 5;
  const text = String(docLabel);
  const match = DOCUMENT_STEP_MAP.find((entry) => entry.pattern.test(text));
  return match?.step ?? 5;
};

export const getResubmitStepFromDocuments = (missingDocuments = []) => {
  if (!Array.isArray(missingDocuments) || missingDocuments.length === 0) return null;
  const steps = missingDocuments.map(getStepForDocument);
  return Math.min(...steps);
};

export const getResubmitInfoFromApplication = (application) => {
  const issues = getAppRequirementIssues(application);
  if (!issues.length) return null;

  const missingDocuments = formatRequirementLabels(issues);
  const step = getResubmitStepFromDocuments(missingDocuments);
  if (!step) return null;

  return { step, missingDocuments, issues };
};

export const isMissingDocumentNotification = (message) => {
  if (!message) return false;
  return message.notification_type === 'missing_documents' ||
    message.notification_type === 'hired_requirements' ||
    (typeof message.message === 'string' &&
      (message.message.includes('required documents are missing or incomplete') ||
        message.message.includes('onboarding requirement')));
};

export const isHiredRequirementsNotification = (message) => {
  if (!message) return false;
  return message.notification_type === 'hired_requirements' ||
    (typeof message.message === 'string' && message.message.includes('onboarding requirement'));
};

export const parseMissingDocsFromMessage = (message) => {
  if (!message) return [];
  return String(message)
    .split('\n')
    .filter((line) => /^\s*[•-]/.test(line))
    .map((line) => line.replace(/^\s*[•-]+\s*/, '').trim())
    .filter(Boolean);
};

export const getPendingResubmitInfo = (email, messages = []) => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  if (!normalizedEmail) return null;

  const pending = messages
    .filter((m) => (m.email || '').trim().toLowerCase() === normalizedEmail)
    .filter(isMissingDocumentNotification)
    .filter((m) => m.is_read !== true)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  if (!pending.length) return null;

  const latest = pending[0];
  let missingDocuments = Array.isArray(latest.missing_documents) ? latest.missing_documents : [];

  if (!missingDocuments.length && latest.message) {
    missingDocuments = parseMissingDocsFromMessage(latest.message);
  }

  const step = getResubmitStepFromDocuments(missingDocuments);
  if (!step) return null;

  return { step, missingDocuments, notification: latest, source: 'notification' };
};

export const resolveApplicantResubmitInfo = (email, messages = [], application = null) => {
  const fromApplication = getResubmitInfoFromApplication(application);
  if (fromApplication) {
    return { ...fromApplication, source: 'requirements_guard' };
  }

  return getPendingResubmitInfo(email, messages);
};
